import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BackupCodes = ({ codes, onDone }) => {
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<h2>MainSpessartBlick Cockpit – Notfall-Codes</h2><p>Jeder Code ist nur einmal gültig.</p><pre style="font-size:18px">${codes.join("\n")}</pre>`);
    w.document.close();
    w.print();
  };
  return (
    <div className="space-y-4" data-testid="backup-codes-view">
      <h2 className="text-2xl font-bold" style={{ fontFamily: "'Work Sans',sans-serif" }}>Notfall-Codes</h2>
      <p className="text-sm text-muted-foreground">Bitte ausdrucken oder sicher aufbewahren. Falls das Handy verloren geht, kannst du dich mit einem dieser Codes einmalig anmelden. Sie werden nur jetzt angezeigt.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-base bg-muted rounded-xl p-4" data-testid="backup-codes-list">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={print} data-testid="backup-codes-print-btn">Drucken</Button>
        <Button type="button" className="flex-1 rounded-xl" onClick={onDone} data-testid="backup-codes-done-btn">Gespeichert – weiter</Button>
      </div>
    </div>
  );
};

export const TwoFactorStep = ({ mfa, onSuccess, onCancel }) => {
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mfa.setup_required) {
      api.post("/auth/2fa/setup", { mfa_token: mfa.mfa_token })
        .then((r) => setQr(r.data))
        .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    }
  }, [mfa]);

  const verify = async (value) => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token: mfa.mfa_token, code: value });
      if (data.backup_codes) setResult(data);
      else onSuccess(data.user);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submit = (e) => { e.preventDefault(); verify(code); };

  const onCode = (value) => {
    setCode(value);
    const digits = value.replace(/\s/g, "");
    if (/^\d{6}$/.test(digits)) verify(digits);
  };

  const pasteCode = async () => {
    try {
      const text = ((await navigator.clipboard.readText()) || "").trim();
      if (!text) { setErr("Zwischenablage ist leer – bitte zuerst den Code in der Authenticator-App antippen."); return; }
      onCode(text);
    } catch {
      setErr("Einfügen nicht erlaubt – bitte lange ins Code-Feld tippen und „Einsetzen“ wählen.");
    }
  };

  if (result) return <BackupCodes codes={result.backup_codes} onDone={() => onSuccess(result.user)} />;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="twofa-form">
      <div>
        <div className="text-sm uppercase tracking-widest text-muted-foreground">2-Faktor-Anmeldung</div>
        <h2 className="text-2xl font-bold mt-1" style={{ fontFamily: "'Work Sans',sans-serif" }}>
          {mfa.setup_required ? "Authenticator einrichten" : "Code eingeben"}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {mfa.setup_required
            ? "Scanne den QR-Code mit Google Authenticator, Microsoft Authenticator o. ä. und gib den 6-stelligen Code ein."
            : "Gib den 6-stelligen Code aus deiner Authenticator-App ein – oder einen Notfall-Code."}
        </p>
      </div>
      {mfa.setup_required && qr && (
        <div className="flex flex-col items-center gap-2" data-testid="twofa-setup-qr">
          <img src={qr.qr} alt="QR-Code" className="w-48 h-48 rounded-xl border hidden md:block" />
          <a href={qr.uri} className="md:hidden w-full text-center rounded-xl h-11 leading-[2.75rem] bg-primary text-white font-medium" data-testid="twofa-open-app-btn">
            In Authenticator-App öffnen
          </a>
          <p className="md:hidden text-xs text-muted-foreground text-center">Am Handy: Authenticator-App installieren, dann diesen Knopf tippen. Klappt das nicht: Schlüssel kopieren und in der App „Einrichtungsschlüssel eingeben“ wählen.</p>
          <div className="text-xs text-muted-foreground">Manueller Schlüssel:</div>
          <code className="text-xs break-all bg-muted px-2 py-1 rounded" data-testid="twofa-secret">{qr.secret}</code>
          <button type="button" className="text-xs underline text-primary" data-testid="twofa-copy-secret-btn"
            onClick={() => navigator.clipboard?.writeText(qr.secret).then(() => setCopied(true))}>
            {copied ? "Kopiert ✓" : "Schlüssel kopieren"}
          </button>
        </div>
      )}
      <div>
        <Label htmlFor="otp">Code</Label>
        <Input id="otp" autoFocus required autoComplete="one-time-code" inputMode={mfa.setup_required ? "numeric" : "text"}
          value={code} onChange={(e) => onCode(e.target.value)} className="mt-1.5 rounded-xl h-11 tracking-widest text-lg"
          data-testid="twofa-code-input" />
        <Button type="button" variant="outline" onClick={pasteCode} disabled={busy} className="w-full h-11 rounded-xl mt-2" data-testid="twofa-paste-btn">
          Code einfügen
        </Button>
        <p className="text-xs text-muted-foreground mt-1">Tipp: In der Authenticator-App den Code antippen (wird kopiert), zurück ins Cockpit und „Code einfügen“ drücken – die Anmeldung startet automatisch.</p>
      </div>
      {err && <div className="text-sm text-destructive bg-destructive/10 rounded-xl p-3" data-testid="twofa-error">{err}</div>}
      <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl" data-testid="twofa-submit-btn">
        {busy ? "Prüfe…" : "Bestätigen"}
      </Button>
      <button type="button" onClick={onCancel} className="text-xs text-muted-foreground underline w-full" data-testid="twofa-cancel-btn">Zurück zur Anmeldung</button>
    </form>
  );
};
